// ColoringBook — a grid of every coloring page the kid has
// generated. Tap a tile to reopen the canvas with that page's line
// art (and the kid's saved colored layer if they hit SAVE last
// time). Persisted artifacts the family paid for — never lost.
//
// EMOJI-IN-UI EXCEPTION: kids-mode opt-in.

import { useState } from "react";
import { COLOR, FONT } from "../../lib/tokens";
import { useKidColoringPages } from "../../lib/useKidColoringPages";
import { KidColoringCanvas } from "./KidColoringCanvas";
import { KidSvgColoring } from "./KidSvgColoring";

export function ColoringBook({ kidId }) {
  const { pages, loading } = useKidColoringPages(kidId);
  const [openPage, setOpenPage] = useState(null);

  if (loading) {
    return (
      <div style={{ fontFamily: FONT.mono, fontSize: 11, color: COLOR.muted, padding: 12, textAlign: "center" }}>
        loading your drawings…
      </div>
    );
  }

  if (!pages.length) {
    return (
      <div style={{
        padding: "20px 16px",
        textAlign: "center",
        fontFamily: FONT.forma,
        fontSize: 13,
        color: COLOR.muted,
        lineHeight: 1.5,
        maxWidth: 320,
        margin: "0 auto",
      }}>
        Make a craft in Mix and tap <strong>🎨 COLOR THIS</strong> to start your coloring book.
      </div>
    );
  }

  return (
    <div style={{ width: "100%", padding: "8px 12px" }}>
      <div style={{
        fontFamily: FONT.mono,
        fontSize: 10,
        color: COLOR.sub,
        letterSpacing: "0.12em",
        textAlign: "center",
        marginBottom: 10,
      }}>
        MY DRAWINGS · {pages.length}
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
        gap: 10,
      }}>
        {pages.map((p) => (
          p.line_art_url
            ? <ColoringTile key={p.id} page={p} onOpen={() => setOpenPage(p)} />
            : <PendingColoringTile key={p.id} page={p} />
        ))}
      </div>

      {/* Prefer the SVG path-tap coloring when a vector exists;
          fall back to canvas+flood-fill for legacy PNG-only pages. */}
      {openPage && (openPage.line_art_svg_url ? (
        <KidSvgColoring
          kidUserId={kidId}
          coloringPageId={openPage.id}
          lineArtSvgURL={openPage.line_art_svg_url}
          sourceImageURL={openPage.source_image_url || null}
          coloredSvgState={openPage.colored_state || null}
          petName={null}
          comboName={openPage.combo_name}
          onClose={() => setOpenPage(null)}
        />
      ) : (
        <KidColoringCanvas
          kidUserId={kidId}
          coloringPageId={openPage.id}
          lineArtURL={openPage.line_art_url}
          coloredURL={openPage.colored_url || null}
          petName={null}
          comboName={openPage.combo_name}
          onClose={() => setOpenPage(null)}
        />
      ))}
    </div>
  );
}

// Animated placeholder tile. Rendered when a kid_coloring_pages row
// exists but its line_art_url is still null — the edge fn is mid-
// generation. Shimmer + pulse on the emoji to read as "working,"
// combo name at the bottom so the kid can tell WHICH drawing is on
// the way. Realtime sub on useKidColoringPages swaps to the real
// ColoringTile the moment the URL lands.
function PendingColoringTile({ page }) {
  const emojis = [page.combo_a_emoji, page.combo_b_emoji]
    .filter(Boolean)
    .join(" ");
  return (
    <div
      style={{
        position: "relative",
        aspectRatio: "1 / 1",
        background: "#fff",
        border: `2px dashed ${COLOR.border}`,
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: `0 4px 12px ${COLOR.shadowSoft || "rgba(0,0,0,0.08)"}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
      }}
      aria-label={`drawing ${page.combo_name || ""} — still being made`}
      role="status"
    >
      <style>{`
        @keyframes coloringBookShimmer {
          0%   { background-position: -200% 0; }
          100% { background-position:  200% 0; }
        }
        @keyframes coloringBookPulse {
          0%, 100% { transform: scale(1);    opacity: 1;   }
          50%      { transform: scale(1.08); opacity: 0.7; }
        }
      `}</style>
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(115deg, transparent 30%, rgba(155,93,229,0.10) 50%, transparent 70%)",
          backgroundSize: "200% 100%",
          animation: "coloringBookShimmer 1800ms ease-in-out infinite",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          fontSize: 34,
          lineHeight: 1,
          animation: "coloringBookPulse 1400ms ease-in-out infinite",
        }}
      >
        {emojis || "✏️"}
      </div>
      <div style={{
        fontFamily: FONT.mono,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.10em",
        color: COLOR.muted,
        textTransform: "uppercase",
      }}>
        Drawing…
      </div>
      <div style={{
        position: "absolute",
        bottom: 0, left: 0, right: 0,
        padding: "6px 8px",
        background: "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.92) 60%)",
        fontFamily: FONT.mono,
        fontSize: 10,
        fontWeight: 700,
        color: COLOR.ink,
        letterSpacing: "0.04em",
        textAlign: "center",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}>
        {(page.combo_name || "drawing").toUpperCase()}
      </div>
    </div>
  );
}

function ColoringTile({ page, onOpen }) {
  const previewUrl = page.colored_url || page.line_art_url;
  const isColored = !!page.colored_url;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="mcm-focusable"
      style={{
        position: "relative",
        aspectRatio: "1 / 1",
        background: "#fff",
        border: `2px solid ${isColored ? COLOR.leaf : COLOR.border}`,
        borderRadius: 14,
        padding: 0,
        cursor: "pointer",
        overflow: "hidden",
        touchAction: "manipulation",
        boxShadow: `0 4px 12px ${COLOR.shadowSoft || "rgba(0,0,0,0.08)"}`,
      }}
      aria-label={page.combo_name || "drawing"}
    >
      <img
        src={previewUrl}
        alt=""
        loading="lazy"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          background: "#FFFFFF",
        }}
      />
      <div style={{
        position: "absolute",
        bottom: 0, left: 0, right: 0,
        padding: "6px 8px",
        background: "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.92) 60%)",
        fontFamily: FONT.mono,
        fontSize: 10,
        fontWeight: 700,
        color: COLOR.ink,
        letterSpacing: "0.04em",
        textAlign: "center",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}>
        {(page.combo_name || "drawing").toUpperCase()}
      </div>
      {isColored && (
        <span style={{
          position: "absolute",
          top: 6, right: 6,
          width: 22, height: 22,
          borderRadius: 11,
          background: COLOR.leaf,
          color: "#fff",
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
          boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
        }}>
          ✓
        </span>
      )}
    </button>
  );
}
